
import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
import FormInput from '@/components/invoice/InvoiceForm/FormInputNew.vue';
import FormDatepicker from '@/components/form/FormDatepicker.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import CurrencyInput from '@/components/form/FormCurrencyInput.vue';
import FileUpload from '@/components/FileUpload/FileUpload.vue';
import InfoIcon from '@/components/icons/InfoIcon.vue';
import SubmissionDateIcon from '@/components/icons/SubmissionDateIcon.vue';
import CrossErrorIcon from '@/components/icons/CrossErrorIcon.vue';
import Tooltip from '@/components/common/Tooltip.vue';
import TooltipVariants from '@/entities/TooltipVariants';
import GET_PROJECT_BY_COMPANY from '@/apollo/projects/GetProjectByCompanyId.gql';
import GET_COMPANIES from '@/apollo/contractors/SearchCompanyByContractor.gql';
import GET_CONTRACTOR_BY_COMPANY_ID from '@/apollo/contractors/GetContractorByCompanyId.gql';
import { NOTIFICATION } from '@/entities/Events';
import Message from '@/data/notificationsMessages';
import { User } from '@/generated/graphql';
import UiButton from '@/components/button/ui-button.vue';

@Component({
	components: {
		FormInput,
		FormDatepicker,
		FormSelect,
		CurrencyInput,
		FileUpload,
		InfoIcon,
		SubmissionDateIcon,
		CrossErrorIcon,
		Tooltip,
		UiButton,
	},
})
export default class Step1 extends Vue {
	@Prop({ type: Object, default: () => ({}) }) record!: any;

	@Prop({ type: Object, required: true }) readonly currentUser!: User;
	isFormValid = false;

	updateFormValidity(isInvalid) {
		this.isFormValid = !isInvalid;
	}
	TooltipVariants = TooltipVariants;
	processing = false;
	isfileDisable = false;
	form: any = {
		invoice_ref: null,
		company_id: null,
		project_id: null,
		total_hours: null,
		total_amount: null,
		rate: null,
		file: null,
		currency: 'CAD',
		external_link: null,
	};
	editform = {};
	validationErrors: any =
		this.currentUser.role === 'ADMIN'
			? {
					resource_id: '',
					project_id: '',
					invoice_ref: '',
					total_hours: '',
					rate: '',
					total_amount: '',
					external_link: '',
			  }
			: {
					company_id: '',
					project_id: '',
					invoice_ref: '',
					rate: '',
					total_hours: '',
					total_amount: '',
					external_link: '',
			  };
	loadingCompanies = false;
	companyOptions = [];
	resourceOptions = [];
	projectOptions = [];
	currencyOptions = ['CAD'];
	get companyClasses(): object {
		return {
			'input-error': this.validationErrors?.company_id,
		};
	}
	get resourceClasses(): object {
		return {
			'input-error': this.validationErrors?.resource_id,
		};
	}
	get projectClasses(): object {
		return {
			'input-error': this.validationErrors?.project_id,
		};
	}

	@Watch('form.company_id')
	@Watch('form.resource_id')
	onSelectedCompanyChanged() {
		if (this.form.company_id) {
			this.$apollo
				.query({
					query: GET_PROJECT_BY_COMPANY,
					variables: {
						company_id: this.form.company_id.id,
					},
				})
				.then(({ data }) => {
					this.projectOptions = data.project.map((e) => ({
						label: e.client_name ? e.client_name : e.name,
						id: e.id,
					}));
				})
				.catch((error) => {});
		} else {
			this.projectOptions = [];
			this.companyOptions = [];
			this.form.project_id = null;
			this.form.company_id = null;
		}
	}

	validateField(field) {
		if (!this.form[field]) {
			this.validationErrors[field] = 'This field is required';
			return false;
		} else if (
			field === 'external_link' &&
			!this.form.file &&
			!this.isValidUrl(this.form[field])
		) {
			this.validationErrors[field] = 'Please enter a valid URL';
			return false;
		} else {
			this.validationErrors[field] = '';
			return true;
		}
	}

	isValidUrl(url) {
		try {
			new URL(url);
			return true;
		} catch (error) {
			return false;
		}
	}
	validateForm() {
		const fields = [
			'project_id',
			'invoice_ref',
			'total_amount',
			'rate',
			'external_link',
		];
		if (this.currentUser.role === 'ADMIN') {
			fields.push('resource_id');
		} else {
			fields.push('company_id');
		}
		const isValid = fields.every((field) => this.validateField(field));
		return isValid;
	}
	handleFileChange(event, file) {
		if (event) {
			this.form.external_link = event;
			this.form.file = file;
			this.isfileDisable = true;
		} else {
			this.form.external_link = null;
			this.isfileDisable = false;
		}
	}
	isEditMode() {
		return this.$route.query.action === 'edit';
	}

	created() {
		if (this.record && Object.keys(this.record).length > 0) {
			this.form = {
				...this.record,
			};
		}
		this.editform = {
			...this.form,
		};
		if (this.currentUser.role === 'ADMIN') {
			this.form.company_id = {
				id: this.currentUser.admin.company.id,
				label: null,
			};
			this.$apollo
				.query({
					query: GET_CONTRACTOR_BY_COMPANY_ID,
					variables: {
						where: {
							company_id: {
								_eq: this.currentUser.admin.company.id,
							},
						},
					},
				})
				.then(({ data }) => {
					this.resourceOptions = data.contractor.map((e) => ({
						label: `${e?.user?.given_name} ${e?.user?.family_name}`,
						id: e.user.id,
					}));
				})
				.catch((error) => {})
				.finally(() => {});
		} else {
			this.$apollo
				.query({
					query: GET_COMPANIES,
					variables: {
						where: { user_id: { _eq: this.currentUser.id } },
					},
				})
				.then(({ data }) => {
					this.companyOptions = data.contractor.map((e) => ({
						label: e.company.business_name,
						id: e.company.id,
					}));
				})
				.catch((error) => {})
				.finally(() => {
					this.loadingCompanies = false; // Set loading to false
				});
		}
	}
	@Watch('record')
	fillForm() {
		if (this.record && Object.keys(this.record).length > 0) {
			this.form = {
				...this.record,
			};
			if (this.record.file) {
				this.isfileDisable = true;
			}
		}
		this.editform = {
			...this.form,
		};
	}
    handleSubmit() {
		if (this.validateForm()) {
			if (this.isEditMode()) {
				this.$router.replace({ query: {} });
				this.$emit('continue', this.form, 2);
				this.$eventBus.emit(
					NOTIFICATION,
					Message.successMessage(
						`Your edits have been saved and should be reflected below. Please review and submit.`
					)
				);
			} else {
				this.$emit('continue', this.form);
			}
		}
	}
	handleClick() {
		if (this.isEditMode()) {
			this.form = {
				...this.editform,
			};
			this.$router.replace({ query: {} });
			this.$emit('continue', this.form, 2);
		} else {
			this.$router.replace('/invoice');
		}
	}
}
